import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ENV } from "@app/env";
import { AngularFireDatabase } from "angularfire2/database";
import { Observable } from "rxjs/Observable";
import { catchError, retry, tap, switchMap } from "rxjs/operators";
import { AuthData } from "./auth";
import { Utilities } from "./utilities";
import { IClient } from "../shared/models/client.model";
import { of } from "rxjs/observable/of";

@Injectable()
export class PurchasesData {
  private action: string = "update";

  constructor(
    private afDb: AngularFireDatabase,
    private utilities: Utilities,
    private http: HttpClient,
    private authData: AuthData,
  ) { }

  public getAllByClient(clientId: string): Observable<any> {
    // return this.afDb.list(`Clients/${clientId}/UserPaymentHistory`).valueChanges();
    return Observable.fromPromise(this.authData.getUserData().getIdToken())
      .switchMap((token: any) => {
        const headers = new HttpHeaders({ Authorization: token });
        return this.http.get(`${ENV.pay_api_url}/payments/${clientId}?limit=10000&skip=0`, { headers })
          // return this.http.get(`${ENV.pay_api_url}/payments/${clientId}?limit=1000&skip=0`, { headers })
          .pipe(catchError((err) => [this.utilities.consoleDecorator("Error get payment list: ", err, "#004d40")]));
      });
  }

  public getAllShoppingCart(clientId: string) {
    return this.afDb.list(`Clients/${clientId}/ShoppingCart`).valueChanges();
  }

  public getShoppingCartByUser(clientId: string, userId: string) {
    return this.afDb.object(`Clients/${clientId}/ShoppingCart/${userId}`).valueChanges();
  }

  public updateDeliveredItem(userId: string, paymentId: string, itemId: string, clientId: string, type: string) {
    return this.afDb.database
      .ref(`Clients/${clientId}/UserPaymentHistory/${userId}/${paymentId}/summary/${type}/${itemId}/delivered`)
      .transaction((value) => !value);
  }

  /**
   * Se traen estadisticas necesarias de mongo
   * @param clientId
   * @param type
   */
  public getStatsMongo(
    clientId: string,
    action: "VIEW" | "ADD_TO_CART" | "REBUS_LOG",
    entityType: string, type: "list" | "count"): Observable<any> {
    return this.http.post(`${ENV.backend}/stats_ai/events`,
      { type, entity_type: entityType, client_id: clientId, action })
      .pipe(
        tap(() => this.utilities.consoleDecorator(`Stats ${action} to ${type}: `, entityType, "orange")),
        retry(3),
        catchError((err) => [this.utilities.consoleDecorator("Error ai stats: ", err, "orange")]),
      );
  }

  public getLogsPay(clientId: string, action: string = "REBUS_LOG", type: "list" | "count" = "list"): Observable<any> {
    return this.http.post(`${ENV.backend}/logs/search`, { type, client_id: clientId, action })
      .pipe(
        tap(() => this.utilities.consoleDecorator(`Stats pay ${action} to ${type}: `, "", "orange")),
        retry(3),
        catchError((err) => [this.utilities.consoleDecorator("Error pay stats: ", err, "orange")]),
      );
  }

  public getReportPayments(clientId: string) {
    return Observable.fromPromise(this.authData.getUserData().getIdToken())
      .switchMap((token: any) => {
        const headers = new HttpHeaders({ Authorization: token });
        return this.http.get(`${ENV.reports_url}/reports/payments/${clientId}/`, { headers })
          .pipe(catchError((err) => [this.utilities.consoleDecorator("Error report generate: ", err, "orange")]));
      });
    // return this.http.get(`${ENV.backend}/reports/payments/sync/${clientId}`)
    //   .pipe(
    //     retry(3),
    //     catchError((err) => [this.utilities.consoleDecorator("Error report generate: ", err, "orange")]),
    //   );
  }

  public associateAppPay(clientId: string, appId: string): Observable<any> {
    return Observable.fromPromise(this.authData.getUserData().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ Authorization: token });
      return this.http.get(`${ENV.pay_api_url}/v1/paymentez/associate/${clientId}/${appId}`, { headers })
        .pipe(
          retry(3),
          tap(() => this.utilities.consoleDecorator("Init asociate app pay: ", [clientId, appId], "#33691e")),
          catchError((err) => [this.utilities.consoleError("Error asociate app pay: ", err)],
          )).toPromise();
    });
  }

  public sendPaymentNotification(client: string, user: string, payment: string, to: string[]): Observable<any> {
    return Observable.fromPromise(this.authData.getUserData().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ Authorization: token });
      return this.http.post(`${ENV.backend}/notification/payment`,
        { client, user, payment, to },
        { headers },
      ).pipe(
        // retry(3),
        tap(() => this.utilities.consoleDecorator(
          "Send payment notification: ",
          [client, user, payment, to],
          "#33691e")),
        catchError((err) => [this.utilities.consoleError("Error send payment notification: ", err)],
        )).toPromise();
    });
  }

  public unlockShopping(clientId, userId) {
    return this.afDb.database
      .ref(`Clients/${clientId}/ShoppingCart/${userId}/state`)
      .set('pending');
  }

  public sendPaymentNotificationQR(client: string, user: string, payment: string) {
    return Observable.fromPromise(this.authData.getUserData().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ Authorization: token });
      return this.http.post(`${ENV.backend}/notification/ticket`,
        { client, user, payment },
        { headers },
      ).pipe(
        // retry(3),
        tap(() => this.utilities.consoleDecorator(
          "Send payment notification: ",
          [client, user, payment],
          "#33691e")),
        catchError((err) => [this.utilities.consoleError("Error send payment notification: ", err)],
        )).toPromise();
    });
  }

  public getAppRebus(): Observable<any> {
    return Observable.fromPromise(this.authData.getUserData().getIdToken()).switchMap((token: any) => {
      const headers = new HttpHeaders({ Authorization: token });
      return this.http.get(`${ENV.pay_api_url}/v1/paymentez/`, { headers })
        .pipe(
          retry(3),
          tap(() => this.utilities.consoleDecorator(`Get apps Rebus: `, "", "orange")),
          catchError((err) => [this.utilities.consoleError("Error get apps: ", err)]),
        );
    });
  }

  public sendReportPurchase(client: IClient, emailUser: string, zone: string): Observable<any> {
    return this.authData.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });

        return this.http.post(`${ENV.jobs_url}/v1/reports/payments`,
          {
            zone,
            "client": client.id,
            "email": emailUser,
            "subject": 'Reporte de ventas del Cliente ' + client.name
          },
          { headers });
      }),
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/purchases.ts